import React from "react";

interface JourneyCard {
  variant: "white" | "blue" | "black";
  position: "left" | "center" | "right";
  title: React.ReactNode;
  features: string[];
  icon: React.ReactNode;
  buttonLabel: string;
  onButtonClick?: () => void;
}

const CheckIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2 6.5 5 9.5 10 3.5" />
  </svg>
);

const defaultCards: JourneyCard[] = [
  {
    variant: "white",
    position: "left",
    title: <>Event<br />Organizers</>,
    features: [
      "No platform ticket fees",
      "Transparent PSP structure",
      "Split payments",
      "Full brand control",
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 17 9 11 13 15 21 7" />
        <polyline points="14 7 21 7 21 14" />
      </svg>
    ),
    buttonLabel: "Request Structure",
  },
  {
    variant: "blue",
    position: "center",
    title: <>Venues<br />&amp; Sport Clubs</>,
    features: [
      "Season ticket support",
      "Accreditation & access control",
      "Settlement automation",
      "Revenue optimization tools",
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="1.5" />
        <circle cx="12" cy="6" r="1.5" />
        <circle cx="9" cy="11" r="1.5" />
      </svg>
    ),
    buttonLabel: "Book Demo",
  },
  {
    variant: "black",
    position: "right",
    title: <>Enterprise<br />&amp; Custom</>,
    features: [
      "Dedicated infrastructure",
      "API integrations",
      "Custom payment routing",
      "SLA support",
    ],
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5z" />
      </svg>
    ),
    buttonLabel: "Contact Sales",
  },
];

interface JourneyCardsProps {
  cards?: JourneyCard[];
}

const JourneyCards: React.FC<JourneyCardsProps> = ({ cards = defaultCards }) => {
  return (
    <div className="journey-cards-wrapper">
      <style>{`
        @property --jc-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }

        .journey-cards-wrapper {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          background: transparent;
        }

        .journey-cards-container {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          gap: 28px;
          perspective: 2000px;
          perspective-origin: 50% 50%;
        }

        .journey-card-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          transform-style: preserve-3d;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .journey-card-wrapper--left { transform: rotateY(22deg) translateX(8px); }
        .journey-card-wrapper--center { transform: rotateY(0deg) translateY(-10px); z-index: 2; }
        .journey-card-wrapper--right { transform: rotateY(-22deg) translateX(-8px); }

        .journey-card {
          --jc-wiggle-angle: 4.6deg;
          --jc-sheen-opacity: 0.72;
          width: 280px;
          height: 380px;
          border-radius: 18px;
          padding: 28px 26px;
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow:
            0 30px 60px -20px rgba(0, 0, 0, 0.5),
            0 18px 36px -18px rgba(0, 0, 0, 0.35);
          overflow: hidden;
          transform-style: preserve-3d;
          will-change: transform;
          transition:
            box-shadow 0.45s cubic-bezier(0.22, 1, 0.36, 1),
            filter 0.45s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .journey-card::before {
          content: '';
          position: absolute;
          inset: -1.5px;
          border-radius: 19px;
          padding: 1.5px;
          background: conic-gradient(
            from var(--jc-angle, 0deg),
            transparent 0%,
            transparent 70%,
            rgba(255, 255, 255, 0.95) 85%,
            rgba(255, 255, 255, 0.45) 92%,
            transparent 100%
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: 4;
        }

        .journey-card::after {
          content: '';
          position: absolute;
          inset: -38%;
          border-radius: 28px;
          background:
            linear-gradient(
              116deg,
              transparent 0%,
              transparent 29%,
              rgba(255,255,255,0.18) 38%,
              rgba(110,231,255,0.24) 43%,
              rgba(255,255,255,0.44) 48%,
              rgba(180,130,255,0.22) 53%,
              rgba(255,255,255,0.12) 59%,
              transparent 70%,
              transparent 100%
            ),
            linear-gradient(
              24deg,
              transparent 0%,
              rgba(255,255,255,0.08) 35%,
              rgba(255,255,255,0.18) 48%,
              transparent 64%
            ),
            radial-gradient(ellipse at 18% 18%, rgba(255,255,255,0.14), transparent 42%),
            radial-gradient(ellipse at 86% 78%, rgba(110,231,255,0.12), transparent 48%);
          mix-blend-mode: screen;
          opacity: 0;
          pointer-events: none;
          transform: translate3d(-34%, 24%, 0) rotate(0.001deg);
          z-index: 2;
        }

        .journey-card-wrapper:hover .journey-card {
          animation: jc-card-hover-wiggle 1.05s cubic-bezier(0.22, 1, 0.36, 1) both;
          box-shadow:
            0 38px 74px -22px rgba(0, 0, 0, 0.58),
            0 22px 44px -18px rgba(0, 0, 0, 0.42),
            0 0 42px rgba(110, 231, 255, 0.12);
          filter: saturate(1.04) brightness(1.04);
        }

        .journey-card-wrapper:hover .journey-card::after {
          animation: jc-metal-hover-sheen 1.05s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .journey-card:hover::before {
          opacity: 1;
          animation: jc-rotate-glow 3s linear infinite;
        }

        @keyframes jc-rotate-glow {
          0% { --jc-angle: 0deg; }
          100% { --jc-angle: 360deg; }
        }

        .journey-card-wrapper--left .journey-card {
          --jc-wiggle-angle: 4.2deg;
        }

        .journey-card-wrapper--center .journey-card {
          --jc-wiggle-angle: 4.8deg;
        }

        .journey-card-wrapper--right .journey-card {
          --jc-wiggle-angle: -4.2deg;
        }

        .journey-card--white { --jc-sheen-opacity: 0.58; }
        .journey-card--blue { --jc-sheen-opacity: 0.76; }
        .journey-card--black { --jc-sheen-opacity: 0.66; }

        @keyframes jc-card-hover-wiggle {
          0% {
            transform: rotate3d(1, -1, 0, 0deg) translateZ(0);
          }
          22% {
            transform: rotate3d(1, -1, 0, var(--jc-wiggle-angle)) translateZ(22px);
          }
          52% {
            transform: rotate3d(1, -1, 0, calc(var(--jc-wiggle-angle) * -0.72)) translateZ(14px);
          }
          78% {
            transform: rotate3d(1, -1, 0, calc(var(--jc-wiggle-angle) * 0.34)) translateZ(10px);
          }
          100% {
            transform: rotate3d(1, -1, 0, 0deg) translateZ(0);
          }
        }

        @keyframes jc-metal-hover-sheen {
          0% {
            transform: translate3d(-36%, 26%, 0) rotate(0.001deg);
            opacity: 0;
          }
          18% {
            opacity: calc(var(--jc-sheen-opacity) * 0.55);
          }
          46% {
            transform: translate3d(4%, -2%, 0) rotate(0.001deg);
            opacity: var(--jc-sheen-opacity);
          }
          78% {
            opacity: calc(var(--jc-sheen-opacity) * 0.42);
          }
          100% {
            transform: translate3d(34%, -26%, 0) rotate(0.001deg);
            opacity: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .journey-card-wrapper:hover .journey-card,
          .journey-card::after {
            animation: none;
          }
        }

        .journey-card > * { position: relative; z-index: 3; }

        .journey-card-shading {
          position: absolute;
          inset: 0;
          border-radius: 18px;
          pointer-events: none;
          z-index: 1;
        }

        .journey-card--white {
          background: linear-gradient(155deg, #f5f5f5 0%, #d8d8d8 55%, #b8b8b8 100%);
          color: #1a1a1a;
        }
        .journey-card--white .journey-card-shading {
          background:
            radial-gradient(ellipse at 20% 0%, rgba(255,255,255,0.6), transparent 50%),
            radial-gradient(ellipse at 80% 100%, rgba(0,0,0,0.08), transparent 60%);
        }

        .journey-card--blue {
          background: linear-gradient(155deg, #1e54e8 0%, #1646c8 50%, #0e2f8f 100%);
          color: #ffffff;
        }
        .journey-card--blue .journey-card-shading {
          background:
            radial-gradient(ellipse at 20% 0%, rgba(255,255,255,0.18), transparent 50%),
            radial-gradient(ellipse at 80% 100%, rgba(0,0,0,0.3), transparent 60%);
        }

        .journey-card--black {
          background: linear-gradient(155deg, #2a2a2a 0%, #1a1a1a 55%, #0a0a0a 100%);
          color: #ffffff;
        }
        .journey-card--black .journey-card-shading {
          background:
            radial-gradient(ellipse at 20% 0%, rgba(255,255,255,0.06), transparent 50%),
            radial-gradient(ellipse at 80% 100%, rgba(0,0,0,0.4), transparent 60%);
        }

        .journey-card-icon {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          opacity: 0.9;
        }

        .journey-card-title {
          font-size: 26px;
          font-weight: 400;
          line-height: 1.2;
          letter-spacing: -0.01em;
          margin-top: 32px;
          margin-bottom: auto;
        }

        .journey-card-features {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .journey-card-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 13px;
          line-height: 1.4;
          font-weight: 400;
        }

        .journey-card-feature-check {
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          margin-top: 1px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .journey-card--white .journey-card-feature-item { color: rgba(26,26,26,0.78); }
        .journey-card--white .journey-card-feature-check { background: rgba(0,0,0,0.08); color: #1a1a1a; }
        .journey-card--blue .journey-card-feature-item { color: rgba(255,255,255,0.92); }
        .journey-card--blue .journey-card-feature-check { background: rgba(255,255,255,0.18); color: #ffffff; }
        .journey-card--black .journey-card-feature-item { color: rgba(255,255,255,0.78); }
        .journey-card--black .journey-card-feature-check { background: rgba(255,255,255,0.12); color: #ffffff; }

        /* === GLASS BUTTON === */
        .journey-glass-button {
          width: 220px;
          height: 54px;
          border-radius: 999px;
          border: 1.5px solid rgba(230, 247, 255, 0.46);
          cursor: pointer;
          position: relative;
          color: #ffffff;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0;
          font-family: inherit;

          background:
            linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.018) 48%, rgba(255,255,255,0.055)),
            rgba(202, 226, 238, 0.025);

          backdrop-filter: blur(26px) saturate(175%) brightness(1.12);
          -webkit-backdrop-filter: blur(26px) saturate(175%) brightness(1.12);

          box-shadow:
            0 18px 36px rgba(0, 0, 0, 0.24),
            0 5px 12px rgba(0, 0, 0, 0.16),
            inset 0 1.5px 0 rgba(255, 255, 255, 0.62),
            inset 0 -1.5px 0 rgba(230, 247, 255, 0.28),
            inset 10px 0 26px rgba(255, 255, 255, 0.04),
            inset -10px 0 26px rgba(255, 255, 255, 0.035);

          transition:
            background 0.4s cubic-bezier(0.22, 1, 0.36, 1),
            border-color 0.4s cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1),
            filter 0.4s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          overflow: hidden;
          isolation: isolate;
        }

        .journey-glass-button::before {
          content: '';
          position: absolute;
          inset: 1.5px;
          border-radius: inherit;
          background:
            radial-gradient(ellipse 80% 58% at 50% 0%, rgba(255,255,255,0.22), rgba(255,255,255,0.035) 44%, transparent 78%),
            linear-gradient(90deg, rgba(255,255,255,0.035), transparent 24%, transparent 76%, rgba(255,255,255,0.035));
          pointer-events: none;
          z-index: 1;
          opacity: 0.58;
        }

        .journey-glass-button::after {
          content: '';
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: 6px;
          height: 18px;
          border-radius: 0 0 999px 999px;
          border-bottom: 1px solid rgba(230, 247, 255, 0.44);
          background:
            radial-gradient(ellipse 100% 70% at 50% 100%,
              rgba(230, 247, 255, 0.34) 0%,
              rgba(230, 247, 255, 0.16) 38%,
              transparent 72%);
          -webkit-mask-image:
            linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%),
            linear-gradient(to top, #000 0%, rgba(0,0,0,0.7) 42%, transparent 100%);
          -webkit-mask-composite: source-in;
          mask-image:
            linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%),
            linear-gradient(to top, #000 0%, rgba(0,0,0,0.7) 42%, transparent 100%);
          pointer-events: none;
          z-index: 1;
          filter: blur(0.35px);
          opacity: 0.72;
        }

        .journey-glass-button-text {
          position: relative;
          z-index: 2;
          text-shadow:
            0 1px 2px rgba(0, 0, 0, 0.58),
            0 3px 10px rgba(0, 0, 0, 0.30);
        }

        .journey-glass-button:hover {
          border-color: rgba(239, 251, 255, 0.64);
          background:
            linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.026) 48%, rgba(255,255,255,0.08)),
            rgba(202, 226, 238, 0.04);
          box-shadow:
            0 22px 42px rgba(0, 0, 0, 0.28),
            0 7px 16px rgba(0, 0, 0, 0.18),
            0 0 24px rgba(230, 247, 255, 0.16),
            inset 0 1.5px 0 rgba(255, 255, 255, 0.72),
            inset 0 -1.5px 0 rgba(230, 247, 255, 0.34),
            inset 10px 0 28px rgba(255, 255, 255, 0.09),
            inset -10px 0 28px rgba(255, 255, 255, 0.07);
          filter: brightness(1.06);
          transform: translateY(-2px);
        }

        .journey-glass-button:active {
          transform: translateY(0);
          box-shadow:
            0 8px 18px rgba(0, 0, 0, 0.22),
            inset 0 1px 0 rgba(255, 255, 255, 0.30),
            inset 0 2px 8px rgba(0, 0, 0, 0.12);
        }
      `}</style>

      <div className="journey-cards-container">
        {cards.map((card, idx) => (
          <div
            key={idx}
            className={`journey-card-wrapper journey-card-wrapper--${card.position}`}
          >
            <div className={`journey-card journey-card--${card.variant}`}>
              <div className="journey-card-shading"></div>
              <div className="journey-card-icon">{card.icon}</div>
              <h2 className="journey-card-title">{card.title}</h2>
              <ul className="journey-card-features">
                {card.features.map((feature, i) => (
                  <li key={i} className="journey-card-feature-item">
                    <span className="journey-card-feature-check">
                      <CheckIcon />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              className="journey-glass-button"
              onClick={card.onButtonClick}
            >
              <span className="journey-glass-button-text">{card.buttonLabel}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JourneyCards;
